import React, { useCallback, useEffect, useRef, useState } from "react";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Modal from "../common/Modal";
import addressesService from "../../services/legacy/addressesService";
import customersService from "../../services/legacy/customersService";

// AX4 stores the customer account whose address book holds "generic"
// third-party delivery addresses in CustParameters.DlvAddressCust (see
// Form_AddressSelect / Form_SalesTable "Configurar > Direccion alt.
// generica" in COMDataSyncServices/AXObjects/Forms). IDAX does not sync AX
// parameters yet, so this mirrors the value confirmed for this
// installation. If a company/dataArea uses a different account, this
// becomes the place to make it configurable.
const THIRD_PARTY_ADDRESS_CUSTOMER_ACCOUNT = "DIRECCIONES";

// Address (idax.address) field -> SalesTable delivery field. Mirrors what
// the AX4 "Direccion alt." dialog copies onto the order.
const ADDRESS_FIELD_MAPPING = {
  name: "deliveryname",
  street: "deliverystreet",
  city: "deliverycity",
  zipcode: "deliveryzipcode",
  county: "deliverycounty",
  state: "deliverystate",
  country: "deliverycountry",
  phone: "deliveryphone",
};

const resolveCustomerRecId = async (custAccount, dataAreaId) => {
  const match = await customersService.findExactByAccountNum(custAccount, dataAreaId);
  return match?.recId ?? match?.recid ?? null;
};

const DeliveryAddressPickerModal = ({ isOpen, onClose, custAccount, dataAreaId, onSelect }) => {
  const { t } = useTranslation();
  const [source, setSource] = useState(custAccount ? "own" : "thirdParty");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const recIdCacheRef = useRef({});
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setError("");
      return;
    }
    setSource(custAccount ? "own" : "thirdParty");
  }, [isOpen, custAccount]);

  const loadAddresses = useCallback(async () => {
    const account = source === "own" ? custAccount : THIRD_PARTY_ADDRESS_CUSTOMER_ACCOUNT;
    const trimmedAccount = (account || "").trim();
    if (!trimmedAccount) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const cacheKey = trimmedAccount.toUpperCase();
      let recId = recIdCacheRef.current[cacheKey];
      if (recId === undefined) {
        recId = await resolveCustomerRecId(trimmedAccount, dataAreaId);
        recIdCacheRef.current[cacheKey] = recId;
      }

      if (!recId) {
        setRows([]);
        setError(t("salesOrders.addressPicker.customerNotFound", { account: trimmedAccount }));
        return;
      }

      const filters = { addrrecid: recId };
      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        filters.lookupQuery = trimmedSearch;
        filters.lookupSearchFields = ["name", "street", "city", "zipcode"];
        filters.lookupSearchMode = "or";
      }

      const page = await addressesService.getAll(
        0,
        100,
        [{ field: "name", direction: "asc" }],
        filters,
      );
      // The address book contains rows left over with no usable data (no
      // name, street, city or zipcode) - skip those instead of showing
      // empty entries the user can't identify or search for.
      const usableRows = (page?.content || []).filter((row) =>
        ["name", "street", "city", "zipcode"].some((field) => (row?.[field] || "").toString().trim()),
      );
      setRows(usableRows);
    } catch (ex) {
      setRows([]);
      setError(t("salesOrders.addressPicker.loadError"));
    } finally {
      setLoading(false);
    }
  }, [source, custAccount, dataAreaId, search, t]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      loadAddresses();
    }, 250);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isOpen, loadAddresses]);

  const handleSelect = (row) => {
    const mapped = {};
    Object.entries(ADDRESS_FIELD_MAPPING).forEach(([addressField, orderField]) => {
      const value = row?.[addressField];
      mapped[orderField] = value !== undefined && value !== null ? value : "";
    });
    // Leave the concatenated "deliveryaddress" field untouched (null, not
    // ""): the backend update path (SalesTableServiceGen#update ->
    // EntityMergeUtils.copyNonNullPropertiesWithNullOverrides) treats null
    // fields as "don't touch, keep the persisted value" and only overwrites
    // fields that are present with a real value. An empty string IS a real
    // value, so it would overwrite and clear the field both in IDAX and,
    // via reverse sync, in AX4 - which is what caused it to show blank in
    // AX4 after picking an address. deliveryaddress is not part of
    // reverse_sync before_image either, so a stale value here would also
    // get pushed to AX4 as-is on every save. Per
    // EmailOrderToIdaxProcessor/README.md, when flat delivery fields come
    // from a canonical `address` master record, deliveryaddress should not
    // be informed at all - null reproduces that "don't touch" behavior.
    mapped.deliveryaddress = null;
    onSelect(mapped);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("salesOrders.addressPicker.title")}
      icon={faMapMarkerAlt}
      className="delivery-address-picker-modal modal-xwide"
    >
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        <button
          type="button"
          className={`btn ${source === "own" ? "btn-primary" : "btn-default"}`}
          onClick={() => setSource("own")}
          disabled={!custAccount}
          title={!custAccount ? t("salesOrders.addressPicker.noCustomerYet") : ""}
        >
          {t("salesOrders.addressPicker.ownAddresses")}
        </button>
        <button
          type="button"
          className={`btn ${source === "thirdParty" ? "btn-primary" : "btn-default"}`}
          onClick={() => setSource("thirdParty")}
        >
          {t("salesOrders.addressPicker.thirdPartyAddresses")}
        </button>
      </div>

      <input
        type="text"
        className="form-control"
        style={{ marginBottom: "12px" }}
        placeholder={t("salesOrders.addressPicker.searchPlaceholder")}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        autoFocus
      />

      {error && (
        <div className="field-error-message" style={{ marginBottom: "8px" }}>
          {error}
        </div>
      )}

      <div style={{ maxHeight: "360px", overflow: "auto" }}>
        {loading ? (
          <div>{t("common.loading")}</div>
        ) : rows.length === 0 ? (
          <div>{t("common.noResults")}</div>
        ) : (
          <table className="table table-condensed mb-none">
            <thead>
              <tr>
                <th>{t("addresses.name")}</th>
                <th>{t("addresses.street")}</th>
                <th>{t("addresses.city")}</th>
                <th>{t("addresses.zipcode")}</th>
                <th>{t("addresses.phone")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowKey =
                  row?.recId ?? row?.recid ?? `${row?.name || ""}-${row?.street || ""}`;
                return (
                  <tr key={rowKey}>
                    <td>{row?.name}</td>
                    <td>{row?.street}</td>
                    <td>{row?.city}</td>
                    <td>{row?.zipcode}</td>
                    <td>{row?.phone}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => handleSelect(row)}
                      >
                        {t("common.select")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
};

const DeliveryAddressPickerButton = ({ custAccount, dataAreaId, onSelect }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ marginBottom: "12px" }}>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => setIsOpen(true)}
      >
        <i className="fa fa-map-marker" style={{ marginRight: "6px" }}></i>
        {t("salesOrders.changeDeliveryAddress")}
      </button>
      <DeliveryAddressPickerModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        custAccount={custAccount}
        dataAreaId={dataAreaId}
        onSelect={onSelect}
      />
    </div>
  );
};

export default DeliveryAddressPickerButton;
